import React from "react";
import { FiLock } from "react-icons/fi";

interface NoAccessMessageProps {
	/** Optional hook for the calling surface's test/selector. */
	"data-testid"?: string;
	/** Plain-language explanation of what is unavailable. */
	description: string;
	title?: string;
}

/**
 * PRO-709 — the single, clean denial an RBAC-gated surface renders when the
 * user's module capability is neither `view` nor `edit`.
 *
 * Denial is rendered INSTEAD of the surface, never alongside it: the point of
 * this component is that a user without capability sees one calm message rather
 * than a populated page whose every request 403s into its own error toast.
 *
 * `role="status"` (polite) rather than `alert`: this is the resolved state of
 * the page after permissions load, not an interruption — it is announced when
 * it replaces the loading spinner without hijacking the user's focus.
 */
export const NoAccessMessage = ({
	title = "Access denied",
	description,
	"data-testid": testId = "no-access-message",
}: NoAccessMessageProps) => (
	<div
		className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center"
		data-testid={testId}
		role="status"
	>
		<FiLock aria-hidden="true" className="h-6 w-6 text-gray-500" />
		<h2 className="text-lg font-semibold text-gray-900">{title}</h2>
		<p className="max-w-md text-sm text-gray-600">{description}</p>
	</div>
);
